import { useMotionValue, motion, useSpring, useTransform } from "framer-motion";
import React, { useRef } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import Container from "components/Container";
import Heading from "components/Heading";
import { detailProjects } from '../../data';
import Paragraph from "components/Paragraph";
export const PortfolioDesktop = () => {
    return (
        <Container className="hidden lg:block lg:mt-auto" >
            <Heading className="text-smoke"><span className="bg-lemone">PORT</span>FOLIO</Heading>
            <div className="w-[100%] flex items-center justify-center">
                <div className="w-[100%]">
                    {
                        detailProjects.slice(0, 3).map((portfolio, index) => (
                            <Link
                                key={index}
                                heading={portfolio?.title}
                                year={portfolio?.year}
                                subheading={portfolio?.role}
                                imgSrc={`/assets/images/portfolio/${portfolio?.image}`}
                                href={`/portfolio/${portfolio?.title}`}
                            />
                        ))
                    }
                </div>
            </div>
        </Container>
    );
};

interface LinkProps {
    heading: string;
    imgSrc: string;
    subheading: string;
    href: string;
    year: string;
}

const Link = ({ heading, year, imgSrc, subheading, href }: LinkProps) => {
    const ref = useRef<HTMLAnchorElement | null>(null);

    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const mouseXSpring = useSpring(x);
    const mouseYSpring = useSpring(y);

    const top = useTransform(mouseYSpring, [0.5, -0.5], ["40%", "60%"]);
    const left = useTransform(mouseXSpring, [0.5, -0.5], ["60%", "70%"]);

    const handleMouseMove = (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) => {
        const rect = ref.current!.getBoundingClientRect();

        const width = rect.width;
        const height = rect.height;

        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const xPct = mouseX / width - 0.5;
        const yPct = mouseY / height - 0.5;

        x.set(xPct);
        y.set(yPct);
    };

    return (
        <motion.a
            href={href}
            ref={ref}
            onMouseMove={handleMouseMove}
            initial="initial"
            whileHover="whileHover"
            className="group relative flex items-center justify-between border-b-2 border-neutral-700  transition-colors duration-500 hover:border-smoke md:py-4 4xl:py-8"
        >
            <div>
                <motion.h2 className="font-nebulica text-5xl text-smoke"
                    variants={{
                        initial: { x: 0 },
                        whileHover: { x: 16 },
                    }}
                    transition={{
                        type: "spring",
                        staggerChildren: 0.075,
                        delayChildren: 0.25,
                    }}>{heading}</motion.h2>
                <Paragraph className="text-smoke text-xl font-medium relative z-10 mt-2 block transition-colors duration-500">{subheading}</Paragraph>
            </div>

            <motion.img
                style={{
                    top,
                    left,
                    translateX: "-50%",
                    translateY: "-50%",
                }}
                variants={{
                    initial: { scale: 0, rotate: "-12deg" },
                    whileHover: { scale: 1, rotate: "12deg" },
                }}
                transition={{ type: "spring" }}
                src={imgSrc}
                className="absolute z-0 h-24 w-auto rounded-lg object-cover md:h-48 md:w-auto"
                alt={heading}
            />

            <motion.div
                variants={{
                    initial: {
                        x: "25%",
                        opacity: 0,
                    },
                    whileHover: {
                        x: "0%",
                        opacity: 1,
                    },
                }}
                transition={{ type: "spring" }}
                className="relative z-10 p-4"
            >
                <div className="text-5xl text-smoke">
                    <FontAwesomeIcon icon={faChevronRight} />
                </div>
            </motion.div>
        </motion.a>
    );
};
